import { computed, type Ref } from 'vue';
import type { CrmAction } from '@/types/crm';

const disableLinksScript = '<scr' + `ipt>document.addEventListener("DOMContentLoaded", function() {
  var a = document.querySelectorAll('a'), i;

  for (i = 0; i < a.length; ++i) {
    a[i].setAttribute("target", "_blank");
  }
})</scr` + 'ipt>';

interface MailActionItem {
  message: string;
  language_code: string;
  is_default?: boolean;
}

interface PreviewErrorResponse {
  data?: unknown;
}

interface UseActivitiesMailActionParams {
  actions: Ref<MailActionItem[]>;
  index: Ref<number>;
  previewList: Ref<Array<unknown> | null>;
  emailTemplateId: Ref<string>;
  currentBrand: Ref<string>;
  postEmailTemplatePreview: (payload: {
    site_id: string;
    markdown: string;
    crm_mail_template_id: string;
  }) => Promise<{ data: { payload: { html: string } } }>;
}

export const useActivitiesMailAction = ({
  actions,
  index,
  previewList,
  emailTemplateId,
  currentBrand,
  postEmailTemplatePreview,
}: UseActivitiesMailActionParams) => {
  const stateEnum = {
    loaded: 0,
    loading: 1,
    error: 2,
    not_loaded: 3,
    not_avaliable: 4,
  } as const;

  const currentPreviewState = computed(() => {
    if (!previewList.value) {
      return stateEnum.not_avaliable;
    }

    if (!previewList.value[index.value]) {
      return stateEnum.not_loaded;
    }

    if (previewList.value[index.value] === stateEnum.loading) {
      return stateEnum.loading;
    }

    if (
      previewList.value[index.value] === stateEnum.error
      || typeof previewList.value[index.value] === 'object'
    ) {
      return stateEnum.error;
    }

    return stateEnum.loaded;
  });

  const currentPreviewStateWatcherParams = computed(() => ({
    currentPreviewState: currentPreviewState.value,
    index: index.value,
  }));

  const isActionsSendDisabled = computed(() => (
    actions.value.every((action) => !String(action.message || '').trim())
    || !actions.value[index.value]?.message
  ));

  const getPreviewState = (targetIndex: number) => {
    if (!previewList.value) {
      return stateEnum.not_avaliable;
    }

    if (!previewList.value[targetIndex]) {
      return stateEnum.not_loaded;
    }

    if (previewList.value[targetIndex] === stateEnum.loading) {
      return stateEnum.loading;
    }

    if (
      previewList.value[targetIndex] === stateEnum.error
      || typeof previewList.value[targetIndex] === 'object'
    ) {
      return stateEnum.error;
    }

    return stateEnum.loaded;
  };

  const loadPreview = async (targetIndex: number) => {
    if (!emailTemplateId.value || !previewList.value || previewList.value[targetIndex]) {
      return;
    }

    previewList.value[targetIndex] = stateEnum.loading;

    const payload = {
      site_id: currentBrand.value,
      markdown: String(actions.value[targetIndex]?.message || ''),
      crm_mail_template_id: emailTemplateId.value,
    };

    try {
      const response = await postEmailTemplatePreview(payload);
      if (!previewList.value) {
        return;
      }

      let { html } = response.data.payload;
      const bodyIndex = html.indexOf('</body>');
      html = `${html.slice(0, bodyIndex)}${disableLinksScript}${html.slice(bodyIndex)}`;
      previewList.value[targetIndex] = html;
    } catch (error) {
      if (previewList.value) {
        previewList.value[targetIndex] = (error as PreviewErrorResponse)?.data || stateEnum.error;
      }
    }
  };

  const loadCurrentPreview = () => {
    loadPreview(index.value);
  };

  const handleSendTestEmail = () => ({
    actionGroup: {
      actions: actions.value,
    },
    index: index.value,
  });

  const getDefaultForm = (): CrmAction => ({
    name: '',
    type: 'email',
    message: '',
    email_subject: '',
    email_header: '',
    language_code: 'en',
    is_default: true,
    email_template_id: '',
  } as unknown as CrmAction);

  return {
    stateEnum,
    currentPreviewState,
    currentPreviewStateWatcherParams,
    isActionsSendDisabled,
    getPreviewState,
    loadPreview,
    loadCurrentPreview,
    handleSendTestEmail,
    getDefaultForm,
  };
};
